import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { 
    Send, Image as ImageIcon, ThumbsUp, Heart, PartyPopper, 
    Laugh, MessageCircle, MoreHorizontal, RefreshCw, Users,
    Sparkles, Plus, Clock, Pin, AtSign, Bell, Globe, UserCheck,
    Users2, Building2, ChevronDown, ChevronUp, X, Paperclip, Trash2, Edit3,
    Check, AlertCircle, Minimize2, Maximize2, Download, FileText, File as FileIcon,
    CheckCircle2, Eye, BarChart3, UserX, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// API Functions
const feedAPI = {
    getAvailableAudiences: () => api.get('/feed/audiences'),
    getPosts: (params) => api.get('/feed/posts', { params }),
    createPost: (data) => api.post('/feed/posts', data),
    updatePost: (postId, data) => api.put(`/feed/posts/${postId}`, data),
    deletePost: (postId) => api.delete(`/feed/posts/${postId}`),
    togglePin: (postId) => api.post(`/feed/posts/${postId}/pin`),
    addReaction: (postId, data) => api.post(`/feed/posts/${postId}/reactions`, data),
    addComment: (postId, data) => api.post(`/feed/posts/${postId}/comments`, data),
    deleteComment: (commentId) => api.delete(`/feed/comments/${commentId}`),
    markAsRead: (postId) => api.post(`/feed/posts/${postId}/read`),
    getEngagement: (postId) => api.get(`/feed/posts/${postId}/engagement`),
    uploadAttachment: (postId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/feed/posts/${postId}/attachments`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    getMentionSuggestions: (query) => api.get('/feed/mentions', { params: { q: query } }),
};

const REACTION_EMOJIS = [
    { emoji: '👍', label: "J'aime", color: 'text-blue-500' },
    { emoji: '❤️', label: "J'adore", color: 'text-red-500' },
    { emoji: '🎉', label: 'Bravo', color: 'text-amber-500' },
    { emoji: '😂', label: 'Haha', color: 'text-yellow-500' },
    { emoji: '🔥', label: 'Top', color: 'text-orange-500' },
];

const AUDIENCE_CONFIG = {
    everyone: {
        icon: Globe,
        label: 'Tout le monde',
        color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200',
        description: 'Visible par tous les utilisateurs'
    },
    vp_only: {
        icon: UserCheck,
        label: 'VP uniquement',
        color: 'bg-blue-500/10 text-blue-600 border-blue-200',
        description: 'Visible par les VP et admins'
    },
    team: {
        icon: Users2,
        label: 'Une équipe',
        color: 'bg-purple-500/10 text-purple-600 border-purple-200',
        description: "Membres de l'équipe + admins"
    }
};

// Component for attachment item with error handling
function AttachmentItem({ att, idx, totalCount, API_URL, postId }) {
    const [hasError, setHasError] = useState(false);
    
    const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif'];
    const isImage = att.is_image || (att.image_data_url && att.image_data_url.startsWith('data:image')) 
        || IMAGE_EXTS.includes(att.file_type?.toLowerCase());
    
    if (hasError && isImage) return null;
    
    // Priorité : image_url (nouvelle URL relative, chargée via api.get + AuthImage) 
    //          > image_data_url (legacy base64) > stored_filename direct
    const buildImageUrl = () => {
        if (att.image_url) return att.image_url;  // path relatif, chargé par AuthImage
        if (att.image_data_url) return att.image_data_url;
        if (att.stored_filename) return `${API_URL}/api/uploads/${att.stored_filename}`;
        return null;
    };
    const imageUrl = buildImageUrl();
    // Pour les nouvelles URLs API, on a besoin du token Bearer
    const needsAuth = !!att.image_url && !att.image_data_url;
    
    // Helper: AuthImage charge l'image via fetch avec Bearer puis crée une blob URL.
    const AuthImage = ({ url, onClick, className }) => {
        const [blobUrl, setBlobUrl] = useState(null);
        const [errored, setErrored] = useState(false);
        useEffect(() => {
            let cancelled = false;
            let createdUrl = null;
            (async () => {
                try {
                    // url = chemin relatif type "/feed/posts/.../image"
                    const res = await api.get(url, { responseType: 'blob' });
                    if (cancelled) return;
                    createdUrl = URL.createObjectURL(res.data);
                    setBlobUrl(createdUrl);
                } catch (e) {
                    if (!cancelled) setErrored(true);
                }
            })();
            return () => {
                cancelled = true;
                if (createdUrl) URL.revokeObjectURL(createdUrl);
            };
        }, [url]);
        useEffect(() => {
            if (errored) setHasError(true);
        }, [errored]);
        if (errored) return null;
        if (!blobUrl) {
            return <div className="w-full h-full bg-slate-200 animate-pulse" />;
        }
        return (
            <img
                src={blobUrl}
                alt=""
                loading="lazy"
                className={className}
                onClick={onClick}
                onError={() => setErrored(true)}
            />
        );
    };
    
    if (!isImage) {
        const FILE_COLORS = {
            pdf: 'text-red-600 bg-red-50 border-red-200',
            doc: 'text-blue-600 bg-blue-50 border-blue-200',
            docx: 'text-blue-600 bg-blue-50 border-blue-200',
            xls: 'text-green-600 bg-green-50 border-green-200',
            xlsx: 'text-green-600 bg-green-50 border-green-200',
            ppt: 'text-orange-600 bg-orange-50 border-orange-200',
            pptx: 'text-orange-600 bg-orange-50 border-orange-200',
            txt: 'text-slate-600 bg-slate-50 border-slate-200',
            csv: 'text-teal-600 bg-teal-50 border-teal-200',
        };
        const fileStyle = FILE_COLORS[att.file_type?.toLowerCase()] || 'text-slate-600 bg-slate-50 border-slate-200';
        const fileLabel = att.file_type?.toUpperCase() || '?';
        
        const handleDownload = async () => {
            const tryDownload = async (url) => {
                const response = await api.get(url, { responseType: 'blob' });
                const blob = new Blob([response.data]);
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', att.original_filename || 'fichier');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);
                return true;
            };
            
            const parseErrorBlob = async (err) => {
                try {
                    if (err?.response?.data instanceof Blob) {
                        const text = await err.response.data.text();
                        const json = JSON.parse(text);
                        return { status: err.response.status, detail: json.detail || '' };
                    }
                    return { status: err?.response?.status, detail: err?.response?.data?.detail || '' };
                } catch {
                    return { status: err?.response?.status, detail: '' };
                }
            };
            
            try {
                await tryDownload(`/feed/posts/${postId}/attachments/${att.id}/download`);
            } catch (err) {
                const { status, detail } = await parseErrorBlob(err);
                if (status === 410 || detail.includes('perdu')) {
                    toast.error("Ce fichier a été perdu lors d'une mise à jour. L'auteur doit le re-publier.");
                    return;
                }
                try {
                    await tryDownload(`/walls/posts/${postId}/attachments/${att.id}/download`);
                } catch (err2) {
                    const parsed = await parseErrorBlob(err2);
                    if (parsed.status === 410 || parsed.detail.includes('perdu')) {
                        toast.error("Ce fichier a été perdu lors d'une mise à jour. L'auteur doit le re-publier.");
                    } else {
                        toast.error('Erreur lors du téléchargement du fichier');
                    }
                }
            }
        };
        
        const formatSize = (bytes) => {
            if (!bytes) return '';
            if (bytes < 1024) return `${bytes} o`;
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
            return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
        };
        
        return (
            <div 
                data-testid={`file-attachment-${att.id}`}
                className={cn(
                    "col-span-full flex items-center gap-3 p-3 rounded-lg border cursor-pointer group hover:shadow-sm transition-all",
                    fileStyle
                )}
                onClick={handleDownload}
            >
                <div className="w-11 h-11 rounded-lg bg-white/80 flex items-center justify-center font-bold text-xs shadow-sm border">
                    {fileLabel}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{att.original_filename || 'Fichier'}</p>
                    <p className="text-xs opacity-70">{formatSize(att.file_size)}</p>
                </div>
                <Download className="w-5 h-5 opacity-50 group-hover:opacity-100 transition-opacity shrink-0" />
            </div>
        );
    }
    
    return (
        <div 
            className={cn(
                "relative overflow-hidden rounded-lg bg-slate-100",
                totalCount === 1 && "aspect-video",
                totalCount >= 2 && "aspect-square"
            )}
        >
            {isImage && imageUrl ? (
                needsAuth ? (
                    <AuthImage
                        url={att.image_url}
                        className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                        onClick={() => {
                            // Open in new tab via fetch + blob URL
                            api.get(att.image_url, { responseType: 'blob' })
                                .then(res => {
                                    const url = URL.createObjectURL(res.data);
                                    window.open(url, '_blank');
                                })
                                .catch(() => {});
                        }}
                    />
                ) : (
                    <img 
                        src={imageUrl}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                        onClick={() => {
                            if (att.image_data_url) {
                                const newWindow = window.open();
                                newWindow.document.write(`<img src="${att.image_data_url}" style="max-width:100%; height:auto;">`);
                            } else {
                                window.open(`${API_URL}/api/uploads/${att.stored_filename}`, '_blank');
                            }
                        }}
                        onError={() => setHasError(true)}
                    />
                )
            ) : null}
        </div>
    );
}

// ========== ENGAGEMENT DIALOG (Admin) ==========
function EngagementDialog({ open, onOpenChange, postId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('compris');

    useEffect(() => {
        if (open && postId) {
            setLoading(true);
            feedAPI.getEngagement(postId)
                .then(res => setData(res.data))
                .catch(() => toast.error("Erreur chargement engagement"))
                .finally(() => setLoading(false));
        }
    }, [open, postId]);

    const tabs = [
        { key: 'compris', label: 'Compris', icon: CheckCircle2, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
        { key: 'readers', label: 'Vues', icon: Eye, color: 'text-blue-600', bgColor: 'bg-blue-50' },
        { key: 'reactions', label: 'Réactions', icon: Heart, color: 'text-amber-600', bgColor: 'bg-amber-50' },
        { key: 'inactive', label: 'Inactifs', icon: UserX, color: 'text-red-600', bgColor: 'bg-red-50' },
    ];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-slate-600" />
                        Suivi d'engagement
                    </DialogTitle>
                </DialogHeader>
                
                {loading ? (
                    <div className="py-8 text-center"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
                ) : data ? (
                    <div className="flex-1 min-h-0 space-y-4">
                        {/* Summary grid */}
                        <div className="grid grid-cols-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="engagement-summary">
                            {[
                                { label: 'Vues', value: data.summary.views, total: data.total_audience, color: 'text-blue-600' },
                                { label: 'Réactions', value: data.summary.reactions, color: 'text-amber-600' },
                                { label: 'Compris', value: data.summary.compris, color: 'text-emerald-600' },
                                { label: 'Inactifs', value: data.summary.inactive, color: 'text-red-500' },
                            ].map((stat, i) => (
                                <div key={stat.label} className={cn("p-3 text-center", i < 3 && "border-r border-slate-200")}>
                                    <p className={cn("text-xl font-bold", stat.color)}>{stat.value}</p>
                                    <p className="text-[11px] text-slate-500 font-medium">{stat.label}</p>
                                    {stat.total && <p className="text-[10px] text-slate-400">/ {stat.total}</p>}
                                </div>
                            ))}
                        </div>

                        {/* Tabs */}
                        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                            {tabs.map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={cn(
                                        "flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-xs font-medium transition-all",
                                        activeTab === tab.key ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"
                                    )}
                                    data-testid={`engagement-tab-${tab.key}`}
                                >
                                    <tab.icon className={cn("w-3.5 h-3.5", activeTab === tab.key && tab.color)} />
                                    {tab.label}
                                    <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full", activeTab === tab.key ? tab.bgColor : "bg-slate-200")}>
                                        {data.details[tab.key]?.length || 0}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* User list */}
                        <div className="overflow-y-auto max-h-[280px] space-y-1">
                            {(data.details[activeTab] || []).length === 0 ? (
                                <p className="text-sm text-slate-400 text-center py-6">Aucun utilisateur dans cette catégorie</p>
                            ) : (
                                data.details[activeTab].map((u, i) => (
                                    <div key={u.user_id || i} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors">
                                        <div className="flex items-center gap-2.5">
                                            <Avatar className="w-8 h-8">
                                                <AvatarFallback className="bg-slate-200 text-slate-600 text-xs font-medium">
                                                    {u.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div>
                                                <p className="text-sm font-medium text-slate-800">{u.user_name}</p>
                                                <p className="text-[10px] text-slate-400">{u.role === 'admin_full' ? 'Admin' : u.role === 'vp' ? 'VP' : 'Apporteur'}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            {u.read_at && <p className="text-[10px] text-slate-400">{format(new Date(u.read_at), "dd MMM HH:mm", { locale: fr })}</p>}
                                            {u.reacted_at && <p className="text-[10px] text-slate-400">{u.emoji} {format(new Date(u.reacted_at), "dd MMM HH:mm", { locale: fr })}</p>}
                                            {u.validated_at && <p className="text-[10px] text-emerald-500 font-medium">{format(new Date(u.validated_at), "dd MMM HH:mm", { locale: fr })}</p>}
                                            {activeTab === 'inactive' && <p className="text-[10px] text-red-400">Aucune interaction</p>}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

// ========== POST COMPONENT ==========
function FeedPost({ post, onReact, onComment, onDelete, onEdit, onPin, onMarkRead, currentUser, canModerate }) {
    const [showComments, setShowComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [showReactions, setShowReactions] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [engagementOpen, setEngagementOpen] = useState(false);
    const postRef = useRef(null);
    const readMarked = useRef(false);

    const isAuthor = post.author_id === currentUser?.id;
    const userReaction = post.reactions?.find(r => r.user_id === currentUser?.id);
    const hasCompris = userReaction?.emoji === 'compris';
    const totalReactions = post.reactions?.filter(r => r.emoji !== 'compris')?.length || 0;
    const comprisCount = post.compris_count || post.reactions?.filter(r => r.emoji === 'compris')?.length || 0;
    const totalComments = post.comments?.length || 0;

    // Group reactions by emoji (exclude "compris")
    const reactionGroups = {};
    post.reactions?.forEach(r => {
        if (r.emoji === 'compris') return;
        if (!reactionGroups[r.emoji]) reactionGroups[r.emoji] = [];
        reactionGroups[r.emoji].push(r);
    });

    // Track read via IntersectionObserver
    useEffect(() => {
        if (readMarked.current || post.is_read_by_me) return;
        const el = postRef.current;
        if (!el) return;
        
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !readMarked.current) {
                    readMarked.current = true;
                    onMarkRead?.(post.id);
                    observer.disconnect();
                }
            },
            { threshold: 0.5 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [post.id, post.is_read_by_me, onMarkRead]);

    const handleReaction = (emoji) => {
        onReact(post.id, emoji);
        setShowReactions(false);
    };

    const handleCompris = () => {
        onReact(post.id, 'compris');
    };

    const handleSubmitComment = async () => {
        if (!newComment.trim() || submitting) return;
        setSubmitting(true);
        try {
            await onComment(post.id, newComment);
            setNewComment('');
        } finally {
            setSubmitting(false);
        }
    };

    const getAudienceConfig = () => {
        if (post.audience === 'team' && post.team_name) {
            return { ...AUDIENCE_CONFIG.team, label: post.team_name };
        }
        return AUDIENCE_CONFIG[post.audience] || AUDIENCE_CONFIG.everyone;
    };

    const audienceConfig = getAudienceConfig();
    const AudienceIcon = audienceConfig.icon;

    return (
        <div 
            ref={postRef}
            className={cn(
                "group relative bg-white rounded-2xl border border-slate-200/80 transition-all duration-200",
                "shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)] hover:border-slate-300",
                post.is_pinned && "ring-2 ring-amber-400/40 border-amber-200"
            )}
            data-testid={`feed-post-${post.id}`}
        >
            {/* Pinned indicator */}
            {post.is_pinned && (
                <div className="absolute -top-2.5 left-4 px-2.5 py-0.5 bg-amber-400 text-amber-900 text-[10px] font-semibold rounded-full flex items-center gap-1 shadow-sm">
                    <Pin className="w-3 h-3" />
                    Important
                </div>
            )}

            {/* Header */}
            <div className="p-3 sm:p-5 pb-2 sm:pb-3">
                <div className="flex items-start gap-3">
                    <Avatar className="w-10 h-10 ring-2 ring-offset-1 ring-slate-100">
                        {post.author_avatar ? <AvatarImage src={post.author_avatar} /> : null}
                        <AvatarFallback className="bg-gradient-to-br from-slate-700 to-slate-900 text-white font-semibold text-sm">
                            {post.author_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900 text-[15px]">{post.author_name}</span>
                            <Badge 
                                variant="outline" 
                                className={cn(
                                    "text-[10px] px-1.5 py-0 font-medium",
                                    post.author_role === 'admin_full' && "bg-purple-50 text-purple-700 border-purple-200",
                                    post.author_role === 'vp' && "bg-blue-50 text-blue-700 border-blue-200",
                                    post.author_role === 'apporteur' && "bg-green-50 text-green-700 border-green-200"
                                )}
                            >
                                {post.author_role === 'admin_full' ? 'Admin' : 
                                 post.author_role === 'vp' ? 'VP' : 'Apporteur'}
                            </Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: fr })}
                            </span>
                            <span className="text-slate-300">·</span>
                            <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 gap-1", audienceConfig.color)}>
                                <AudienceIcon className="w-3 h-3" />
                                {audienceConfig.label}
                            </Badge>
                        </div>
                    </div>

                    {/* Actions menu */}
                    {(isAuthor || canModerate) && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <MoreHorizontal className="w-4 h-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                {canModerate && (
                                    <DropdownMenuItem onClick={() => onPin(post.id)} className="gap-2">
                                        <Pin className="w-4 h-4" />
                                        {post.is_pinned ? 'Désépingler' : 'Épingler'}
                                    </DropdownMenuItem>
                                )}
                                {isAuthor && (
                                    <DropdownMenuItem onClick={() => onEdit(post)} className="gap-2">
                                        <Edit3 className="w-4 h-4" />
                                        Modifier
                                    </DropdownMenuItem>
                                )}
                                {(isAuthor || canModerate) && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                            onClick={() => onDelete(post.id)} 
                                            className="gap-2 text-red-600 focus:text-red-600"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Supprimer
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="px-3 sm:px-5 pb-3 sm:pb-4">
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed text-sm sm:text-[15px]">
                    {post.content}
                </p>
            </div>

            {/* Attachments */}
            {post.attachments?.length > 0 && (
                <div className="px-3 sm:px-5 pb-3 sm:pb-4">
                    <div className={cn(
                        "grid gap-2",
                        post.attachments.length === 1 && "grid-cols-1",
                        post.attachments.length === 2 && "grid-cols-2",
                        post.attachments.length >= 3 && "grid-cols-2"
                    )}>
                        {post.attachments.slice(0, 4).map((att, idx) => (
                            <AttachmentItem 
                                key={att.id || idx}
                                att={att}
                                idx={idx}
                                totalCount={post.attachments.length}
                                API_URL={API_URL}
                                postId={post.id}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Stats bar (reactions count + compris count + admin engagement) */}
            {(totalReactions > 0 || comprisCount > 0 || totalComments > 0) && (
                <div className="px-3 sm:px-5 py-2 sm:py-2.5 flex items-center justify-between text-xs text-slate-500 border-t border-slate-100">
                    <div className="flex items-center gap-3">
                        {totalReactions > 0 && (
                            <div className="flex items-center gap-1.5">
                                <div className="flex -space-x-1">
                                    {Object.keys(reactionGroups).slice(0, 3).map(emoji => (
                                        <span key={emoji} className="text-sm bg-white rounded-full shadow-sm border px-1">{emoji}</span>
                                    ))}
                                </div>
                                <span className="text-slate-600 font-medium">{totalReactions}</span>
                            </div>
                        )}
                        {comprisCount > 0 && (
                            <div className="flex items-center gap-1 text-emerald-600" data-testid={`compris-count-${post.id}`}>
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                <span className="font-semibold">{comprisCount}</span>
                                <span className="text-emerald-500 text-[11px]">compris</span>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Admin: engagement button */}
                        {canModerate && (
                            <button 
                                onClick={() => setEngagementOpen(true)}
                                className="flex items-center gap-1 text-slate-400 hover:text-slate-600 transition-colors"
                                data-testid={`engagement-btn-${post.id}`}
                            >
                                <BarChart3 className="w-3.5 h-3.5" />
                                <span className="text-[11px]">{post.read_count || 0} vues</span>
                            </button>
                        )}
                        {totalComments > 0 && (
                            <button 
                                onClick={() => setShowComments(!showComments)}
                                className="hover:text-slate-700 hover:underline transition-colors"
                            >
                                {totalComments} commentaire{totalComments > 1 ? 's' : ''}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Actions Bar */}
            <div className="px-2 sm:px-3 py-2 flex items-center gap-1 border-t border-slate-100">
                {/* Emoji Reaction button */}
                <div className="relative flex-1 min-w-0">
                    <Popover open={showReactions} onOpenChange={setShowReactions}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className={cn(
                                    "w-full justify-center gap-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg h-9 px-2",
                                    userReaction && userReaction.emoji !== 'compris' && "text-blue-600"
                                )}
                                data-testid={`reaction-button-${post.id}`}
                            >
                                {userReaction && userReaction.emoji !== 'compris' ? (
                                    <span className="text-base">{userReaction.emoji}</span>
                                ) : (
                                    <ThumbsUp className="w-4 h-4 shrink-0" />
                                )}
                                <span className="text-xs sm:text-sm font-medium">Réagir</span>
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-1.5 flex gap-0.5" side="top">
                            {REACTION_EMOJIS.map(r => (
                                <button
                                    key={r.emoji}
                                    onClick={() => handleReaction(r.emoji)}
                                    className="text-2xl hover:scale-125 transition-transform p-1.5 rounded-lg hover:bg-slate-100"
                                    title={r.label}
                                >
                                    {r.emoji}
                                </button>
                            ))}
                        </PopoverContent>
                    </Popover>
                </div>

                {/* Comment button */}
                <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 min-w-0 justify-center gap-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg h-9 px-2"
                    onClick={() => setShowComments(!showComments)}
                    data-testid={`comment-button-${post.id}`}
                >
                    <MessageCircle className="w-4 h-4 shrink-0" />
                    <span className="text-xs sm:text-sm font-medium hidden xs:inline">Commenter</span>
                </Button>

                {/* "Compris et intégré" button - DISTINCT */}
                <Button
                    size="sm"
                    onClick={handleCompris}
                    className={cn(
                        "flex-1 min-w-0 justify-center gap-1.5 rounded-lg h-9 font-medium transition-all px-2",
                        hasCompris
                            ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
                            : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                    )}
                    data-testid={`compris-button-${post.id}`}
                >
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-sm hidden sm:inline">Compris et intégré</span>
                    <span className="text-sm sm:hidden">Compris</span>
                </Button>
            </div>

            {/* Comments Section */}
            {showComments && (
                <div className="px-3 sm:px-5 py-3 bg-slate-50/80 border-t border-slate-100 space-y-3 rounded-b-lg">
                    {post.comments?.map(comment => (
                        <div key={comment.id} className="flex gap-2 group/comment">
                            <Avatar className="w-7 h-7">
                                <AvatarFallback className="bg-slate-200 text-slate-600 text-[10px] font-medium">
                                    {comment.author_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                                <div className="bg-white rounded-lg px-3 py-2 shadow-sm border border-slate-100">
                                    <span className="font-medium text-slate-900 text-sm">{comment.author_name}</span>
                                    <p className="text-slate-700 text-sm">{comment.content}</p>
                                </div>
                                <span className="text-[10px] text-slate-400 ml-2">
                                    {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: fr })}
                                </span>
                            </div>
                        </div>
                    ))}

                    <div className="flex gap-2 pt-1">
                        <Avatar className="w-7 h-7">
                            <AvatarFallback className="bg-slate-800 text-white text-[10px] font-medium">
                                {currentUser?.first_name?.[0]}{currentUser?.last_name?.[0]}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 flex gap-2">
                            <Input
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder="Écrire un commentaire..."
                                className="flex-1 bg-white rounded-full text-sm h-8 border-slate-200 focus-visible:ring-slate-300"
                                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmitComment()}
                            />
                            <Button
                                size="sm"
                                onClick={handleSubmitComment}
                                disabled={!newComment.trim() || submitting}
                                className="rounded-full bg-slate-800 hover:bg-slate-700 h-8 w-8 p-0"
                            >
                                <Send className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Engagement Dialog */}
            {canModerate && (
                <EngagementDialog 
                    open={engagementOpen} 
                    onOpenChange={setEngagementOpen} 
                    postId={post.id} 
                />
            )}
        </div>
    );
}

// ========== NEW POST DIALOG ==========
function NewPostDialog({ 
    open, 
    onOpenChange, 
    onSubmit, 
    audiences, 
    teams,
    editingPost = null 
}) {
    const [content, setContent] = useState('');
    const [selectedAudience, setSelectedAudience] = useState('everyone');
    const [selectedTeam, setSelectedTeam] = useState('');
    const [files, setFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (editingPost) {
            setContent(editingPost.content || '');
            setSelectedAudience(editingPost.audience || 'everyone');
            setSelectedTeam(editingPost.team_id || '');
        } else {
            setContent('');
            setSelectedAudience('everyone');
            setSelectedTeam('');
            setFiles([]);
        }
    }, [editingPost, open]);

    const handleSubmit = async () => {
        if (!content.trim()) return;
        if (selectedAudience === 'team' && !selectedTeam) {
            toast.error('Veuillez sélectionner une équipe.');
            return;
        }
        setSubmitting(true);
        try {
            await onSubmit({
                content: content.trim(),
                audience: selectedAudience,
                team_id: selectedAudience === 'team' ? selectedTeam : null,
                files
            }, editingPost?.id);
            setContent('');
            setFiles([]);
            onOpenChange(false);
        } catch (err) {
            toast.error('Erreur lors de la publication');
        } finally {
            setSubmitting(false);
        }
    };

    const handleFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (selectedFiles.length + files.length > 4) {
            toast.error('Maximum 4 fichiers par publication');
            return;
        }
        setFiles([...files, ...selectedFiles]);
    };

    const removeFile = (index) => {
        setFiles(files.filter((_, i) => i !== index));
    };

    const availableAudiences = audiences.filter(a => {
        if (a.type === 'everyone') return true;
        if (a.type === 'vp_only') return true;
        if (a.type === 'team') return true;
        return false;
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-amber-500" />
                        {editingPost ? 'Modifier la publication' : 'Nouvelle publication'}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <Textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Partagez une actualité, une victoire, une annonce..."
                        className="min-h-[120px] resize-none text-[15px] border-slate-200 focus-visible:ring-slate-400"
                        autoFocus
                    />

                    {files.length > 0 && (
                        <div className="space-y-2">
                            {files.map((file, idx) => {
                                const isImage = file.type.startsWith('image/');
                                const ext = file.name.split('.').pop()?.toUpperCase() || '?';
                                const sizeStr = file.size < 1024*1024 
                                    ? `${(file.size/1024).toFixed(1)} Ko` 
                                    : `${(file.size/(1024*1024)).toFixed(1)} Mo`;
                                
                                return isImage ? (
                                    <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden bg-slate-100 inline-block mr-2">
                                        <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" />
                                        <button onClick={() => removeFile(idx)} className="absolute top-1 right-1 w-5 h-5 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ) : (
                                    <div key={idx} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                                        <div className="w-9 h-9 rounded bg-red-50 text-red-600 flex items-center justify-center text-xs font-bold">{ext}</div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-slate-800 truncate">{file.name}</p>
                                            <p className="text-xs text-slate-500">{sizeStr}</p>
                                        </div>
                                        <button onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-500 shrink-0">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Audience</label>
                        <div className="grid grid-cols-3 gap-2">
                            {availableAudiences.map(audience => {
                                const config = AUDIENCE_CONFIG[audience.type];
                                if (!config) return null;
                                const Icon = config.icon;
                                return (
                                    <button
                                        key={audience.type}
                                        onClick={() => setSelectedAudience(audience.type)}
                                        className={cn(
                                            "p-3 rounded-lg border-2 transition-all text-left",
                                            selectedAudience === audience.type
                                                ? "border-slate-800 bg-slate-50"
                                                : "border-slate-200 hover:border-slate-300"
                                        )}
                                    >
                                        <Icon className={cn("w-5 h-5 mb-1", selectedAudience === audience.type ? "text-slate-800" : "text-slate-400")} />
                                        <p className="text-sm font-medium text-slate-800">{config.label}</p>
                                        <p className="text-[10px] text-slate-500 mt-0.5">{config.description}</p>
                                    </button>
                                );
                            })}
                        </div>

                        {selectedAudience === 'team' && teams.length > 0 && (
                            <select
                                value={selectedTeam}
                                onChange={(e) => setSelectedTeam(e.target.value)}
                                className="w-full mt-2 p-2 border rounded-lg text-sm"
                            >
                                <option value="">Sélectionner une équipe</option>
                                {teams.map(team => (
                                    <option key={team.id} value={team.id}>{team.name}</option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    <div className="flex gap-2 flex-1">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                            multiple
                            className="hidden"
                        />
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={files.length >= 4}
                            className="gap-2"
                            data-testid="attach-file-btn"
                        >
                            <Paperclip className="w-4 h-4" />
                            Fichier
                        </Button>
                    </div>
                    <Button
                        onClick={handleSubmit}
                        disabled={!content.trim() || submitting || (selectedAudience === 'team' && !selectedTeam)}
                        className="bg-slate-800 hover:bg-slate-700 gap-2"
                    >
                        {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {editingPost ? 'Modifier' : 'Publier'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ========== MAIN WIDGET ==========
// Cache léger global pour éviter le rechargement entre navigations
const __feedCache = { data: null, ts: 0 };
const __FEED_CACHE_MS = 30 * 1000;

export function NewsFeedWidget({ forceExpanded = false }) {
    const { user, refreshUnreadFeedCount } = useAuth();
    const [posts, setPosts] = useState(__feedCache.data?.posts || []);
    const [loading, setLoading] = useState(!__feedCache.data);
    const [audiences, setAudiences] = useState(__feedCache.data?.audiences || []);
    const [teams, setTeams] = useState(__feedCache.data?.teams || []);
    const [showNewPostDialog, setShowNewPostDialog] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem('newsfeed_collapsed');
        return saved === 'true';
    });

    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const isVP = user?.role === 'vp';
    
    const toggleCollapsed = () => {
        const newValue = !isCollapsed;
        setIsCollapsed(newValue);
        localStorage.setItem('newsfeed_collapsed', String(newValue));
    };

    // Sur la page dédiée (forceExpanded), le fil reste toujours déployé.
    const effectiveCollapsed = forceExpanded ? false : isCollapsed;

    useEffect(() => {
        // Si cache valide, ne pas recharger
        if (__feedCache.data && (Date.now() - __feedCache.ts) < __FEED_CACHE_MS) {
            return;
        }
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [audiencesRes, postsRes] = await Promise.all([
                feedAPI.getAvailableAudiences(),
                feedAPI.getPosts({ limit: 20 })
            ]);
            const newAudiences = audiencesRes.data?.audiences || [];
            const newTeams = audiencesRes.data?.teams || [];
            const newPosts = postsRes.data?.posts || [];
            setAudiences(newAudiences);
            setTeams(newTeams);
            setPosts(newPosts);
            __feedCache.data = { audiences: newAudiences, teams: newTeams, posts: newPosts };
            __feedCache.ts = Date.now();
        } catch (err) {
            console.error('Erreur chargement feed:', err);
            try {
                const walls = ['direction', 'direction_vp', 'team'];
                const allPosts = [];
                for (const wall of walls) {
                    try {
                        const res = await api.get(`/walls/${wall}/posts`, { params: { limit: 10 } });
                        if (res.data?.posts || res.data) {
                            const wallPosts = res.data?.posts || res.data;
                            allPosts.push(...wallPosts.map(p => ({ 
                                ...p, 
                                audience: wall === 'direction' ? 'everyone' : wall === 'direction_vp' ? 'vp_only' : 'team'
                            })));
                        }
                    } catch (e) {}
                }
                allPosts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                setPosts(allPosts.slice(0, 15));
                const defaultAudiences = [{ type: 'everyone' }];
                if (isAdmin || isVP) defaultAudiences.push({ type: 'vp_only' });
                defaultAudiences.push({ type: 'team' });
                setAudiences(defaultAudiences);
            } catch (e) {}
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        __feedCache.ts = 0; // invalider le cache pour forcer le reload
        await loadData();
        setRefreshing(false);
    };

    const handleCreatePost = async (data, editId = null) => {
        try {
            if (editId) {
                await feedAPI.updatePost(editId, { content: data.content });
                toast.success('Publication modifiée');
            } else {
                try {
                    const res = await feedAPI.createPost({
                        content: data.content,
                        audience: data.audience,
                        team_id: data.team_id
                    });
                    const postId = res?.data?.post_id;
                    if (data.files?.length > 0 && postId) {
                        for (const file of data.files) {
                            await feedAPI.uploadAttachment(postId, file);
                        }
                    }
                } catch (apiErr) {
                    const wallType = data.audience === 'vp_only' ? 'direction_vp' : 
                                     data.audience === 'team' ? 'team' : 'direction';
                    const wallRes = await api.post('/walls/posts', {
                        wall_type: wallType,
                        team_id: data.team_id,
                        content: data.content
                    });
                    const wallPostId = wallRes?.data?.post_id;
                    if (data.files?.length > 0 && wallPostId) {
                        for (const file of data.files) {
                            const formData = new FormData();
                            formData.append('file', file);
                            await api.post(`/walls/posts/${wallPostId}/attachments`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                            });
                        }
                    }
                }
                toast.success('Publication créée !');
            }
            setEditingPost(null);
            await loadData();
        } catch (err) {
            throw err;
        }
    };

    const handleReaction = async (postId, emoji) => {
        try {
            try {
                await feedAPI.addReaction(postId, { emoji });
            } catch (e) {
                await api.post(`/walls/posts/${postId}/reactions`, { emoji });
            }
            await loadData();
            // La pastille rouge ne disparaît qu'après une interaction (réaction)
            refreshUnreadFeedCount?.();
        } catch (err) {
            toast.error('Erreur');
        }
    };

    const handleComment = async (postId, content) => {
        try {
            try {
                await feedAPI.addComment(postId, { content });
            } catch (e) {
                await api.post(`/walls/posts/${postId}/comments`, { content });
            }
            await loadData();
            // Un commentaire compte aussi comme une interaction → met à jour la pastille
            refreshUnreadFeedCount?.();
        } catch (err) {
            toast.error('Erreur');
        }
    };

    const handleDelete = async (postId) => {
        if (!confirm('Supprimer cette publication ?')) return;
        try {
            try {
                await feedAPI.deletePost(postId);
            } catch (e) {
                await api.delete(`/walls/posts/${postId}`);
            }
            toast.success('Publication supprimée');
            await loadData();
        } catch (err) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handlePin = async (postId) => {
        try {
            await feedAPI.togglePin(postId);
            await loadData();
        } catch (err) {
            toast.error('Fonctionnalité non disponible');
        }
    };

    const handleEdit = (post) => {
        setEditingPost(post);
        setShowNewPostDialog(true);
    };

    const handleMarkRead = useCallback(async (postId) => {
        try {
            await feedAPI.markAsRead(postId);
            // Mettre à jour la pastille du menu "Fil d'actualité"
            refreshUnreadFeedCount?.();
        } catch (e) {
            // Silent fail
        }
    }, [refreshUnreadFeedCount]);

    if (loading) {
        return (
            <Card className="col-span-full" data-testid="news-feed-widget">
                <div className="p-6 space-y-4">
                    <Skeleton className="h-8 w-48" />
                    {[1, 2, 3].map(i => (
                        <Skeleton key={i} className="h-40 rounded-lg" />
                    ))}
                </div>
            </Card>
        );
    }

    const sortedPosts = [...posts].sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        return new Date(b.created_at) - new Date(a.created_at);
    });

    return (
        <>
            <Card 
                className="col-span-full overflow-hidden rounded-2xl border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.05)] bg-white" 
                data-testid="news-feed-widget"
            >
                {/* Header */}
                <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-b border-slate-100 bg-gradient-to-r from-[#0B1F3B] to-[#13345c]">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15 shrink-0">
                                <Sparkles className="w-4 h-4 text-amber-300" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-sm sm:text-base font-bold text-white flex items-center gap-2">
                                    <span className="truncate">Fil d'actualité</span>
                                    {posts.length > 0 && (
                                        <Badge variant="secondary" className="font-normal text-xs bg-white/15 text-white border-0">{posts.length}</Badge>
                                    )}
                                </h2>
                                <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-300">
                                    <img src="/icons/icon-192x192.png" alt="" className="h-3.5 w-3.5 rounded-[3px] object-contain" />
                                    {effectiveCollapsed ? 'Cliquez pour afficher' : 'Valnexo · Communications internes'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {!forceExpanded && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={toggleCollapsed}
                                    className="text-white/80 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                                    title={isCollapsed ? "Afficher le fil" : "Réduire le fil"}
                                    data-testid="toggle-feed-btn"
                                >
                                    {isCollapsed ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                                </Button>
                            )}
                            {!effectiveCollapsed && (
                                <>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleRefresh}
                                        disabled={refreshing}
                                        className="text-white/80 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                                    >
                                        <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
                                    </Button>
                                    <Button
                                        onClick={() => { setEditingPost(null); setShowNewPostDialog(true); }}
                                        size="sm"
                                        className="bg-amber-500 hover:bg-amber-600 text-white gap-1 h-8 px-2 sm:px-3"
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                        <span className="hidden sm:inline">Publier</span>
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Posts */}
                {!effectiveCollapsed && (
                <CardContent className="p-3 sm:p-4 space-y-3 sm:space-y-4 max-h-[700px] overflow-y-auto bg-slate-50/50">
                    {sortedPosts.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                                <Sparkles className="w-7 h-7 text-slate-300" />
                            </div>
                            <h3 className="font-medium text-slate-700 mb-1">Aucune publication</h3>
                            <p className="text-sm text-slate-500 mb-4">Soyez le premier à partager une actualité !</p>
                            <Button onClick={() => setShowNewPostDialog(true)} variant="outline" className="gap-2">
                                <Plus className="w-4 h-4" />
                                Créer une publication
                            </Button>
                        </div>
                    ) : (
                        sortedPosts.map(post => (
                            <div key={post.id} style={{ minHeight: 220 }}>
                                <FeedPost
                                    post={post}
                                    onReact={handleReaction}
                                    onComment={handleComment}
                                    onDelete={handleDelete}
                                    onEdit={handleEdit}
                                    onPin={handlePin}
                                    onMarkRead={handleMarkRead}
                                    currentUser={user}
                                    canModerate={isAdmin}
                                />
                            </div>
                        ))
                    )}
                </CardContent>
                )}
            </Card>

            <NewPostDialog
                open={showNewPostDialog}
                onOpenChange={setShowNewPostDialog}
                onSubmit={handleCreatePost}
                audiences={audiences}
                teams={teams}
                editingPost={editingPost}
            />
        </>
    );
}

export default NewsFeedWidget;
